import { CommentOutlined, FileTextOutlined, MessageOutlined, SnippetsOutlined } from '@ant-design/icons';
import { Col, Collapse, Form, Input, InputNumber, Row, Select } from 'antd';
import FormDependency, { shouldUpdateDependency } from 'components/FormDependency';
import NumberInput from 'components/NumberInput';
import React, { Fragment } from 'react';
import { FormattedMessage } from 'react-intl';

import useFormatMessage from 'hooks/useFormatMessage';
import TranslationTabs from 'components/TranslationTabs';
import DeviceTypeIcons from 'components/DeviceTypeIcons';
import { DeviceType, SurveySetting, SurveyType } from 'shared/prisma/types';
import InputInfoCircle from 'components/InputInfoCircle';
import messages from './messages';

const { Panel } = Collapse;

const BUTTON_POSITIONS = ['centerRight', 'bottomLeft', 'bottomRight'];

const BUTTON_STYLES = ['tab', 'floatingButton'];

const BUTTON_ICON_OPTIONS = [
  { value: null, label: 'None' },
  { value: 'comment', label: <MessageOutlined /> },
  { value: 'comments', label: <CommentOutlined /> },
  { value: 'clipboard', label: <SnippetsOutlined /> },
  { value: 'list-alt', label: <FileTextOutlined /> },
];

const settingsOptions = [
  { label: 'Default settings', value: SurveySetting.Default },
  { label: 'Use desktop settings', value: SurveySetting.Desktop },
  { label: 'Use tablet settings', value: SurveySetting.Tablet },
  { label: 'Use mobile settings', value: SurveySetting.Mobile },
  { label: 'Custom settings', value: SurveySetting.Custom },
  { label: 'Disabled', value: 'DISABLED' },
];

const translationSettingsOptions = [
  { label: 'Use primary language settings', value: SurveySetting.Primary },
  ...settingsOptions,
];

function getOptions({ device, isTranslation }) {
  return (isTranslation ? translationSettingsOptions : settingsOptions).filter(o => o.value !== SurveySetting[device]);
}

const TimerForm = ({ namePath, disabled }) => (
  <Row gutter={16}>
    <Col span={12}>
      <Form.Item name={[...namePath, 'delay']} label={<FormattedMessage {...messages.delay} />}>
        <NumberInput
          min={0}
          addonAfter="seconds"
          disabled={disabled}
          parse={value => value?.toString()}
        />
      </Form.Item>
    </Col>
    {/* <Col span={6}>
    <Form.Item name={[...namePath, 'sessionDelay']} label={<FormattedMessage {...messages.sessionDelay} />}>
      <InputNumber
        min={0}
        addonAfter="seconds"
        disabled={disabled}
      />
    </Form.Item>
  </Col> */}
    <Col span={12}>
      <Form.Item name={[...namePath, 'declineFrequency']} label={<FormattedMessage {...messages.declineFrequency} />}>
        <NumberInput
          factor={60 * 60 * 24}
          min={0}
          addonAfter="days"
          disabled={disabled}
        />
      </Form.Item>
    </Col>
    <Col span={12}>
      <Form.Item name={[...namePath, 'frequency']} label={<FormattedMessage {...messages.frequency} />}>
        <NumberInput
          factor={60 * 60 * 24}
          min={0}
          addonAfter="days"
          disabled={disabled}
          parse={value => value?.toString()}
        />
      </Form.Item>
    </Col>
  </Row>
);

const PortionForm = ({ namePath, disabled }) => (
  <Row gutter={16}>
    <Col span={6}>
      <Form.Item name={[...namePath, 'portion']} label={<FormattedMessage {...messages.portion} />}>
        <InputNumber addonAfter="%" disabled={disabled} min={0} max={100} />
      </Form.Item>
    </Col>
  </Row>
);

const GeneralSurveyForm = ({ namePath, disabled }) => (
  <>
    <PortionForm namePath={namePath} disabled={disabled} />
    <TimerForm namePath={namePath} disabled={disabled} />
  </>
);

const FeedbackForm = ({ namePath, disabled }) => (
  <>
    <PortionForm namePath={namePath} disabled={disabled} />
    <Row gutter={16}>
      <Col span={8}>
        <Form.Item name={[...namePath, 'triggerStyle']} label="Button type">
          <Select options={BUTTON_STYLES.map(value => ({ label: <FormattedMessage {...messages[value]} />, value }))} disabled={disabled} />
        </Form.Item>
      </Col>
      <Col span={8}>
        <Form.Item name={[...namePath, 'triggerPosition']} label="Button position">
          <Select options={BUTTON_POSITIONS.map(value => ({ label: <FormattedMessage {...messages[value]} />, value }))} disabled={disabled} />
        </Form.Item>
      </Col>
      <FormDependency name={[...namePath, 'triggerStyle']} show={value => value === 'floatingButton'}>
        <Col span={8}>
          <Form.Item name={[...namePath, 'buttonIcon']} label="Button icon">
            <Select options={BUTTON_ICON_OPTIONS} disabled={disabled} />
          </Form.Item>
        </Col>
      </FormDependency>
      <FormDependency name={[...namePath, 'triggerStyle']} show={value => value === 'tab'}>
        <Col span={8}>
          <Form.Item name={[...namePath, 'buttonLabel']} label={<FormattedMessage {...messages.buttonLabel} />}>
            <Input disabled={disabled} maxLength={100} />
          </Form.Item>
        </Col>
      </FormDependency>
    </Row>
  </>
);

const TargetedSurveyForm = ({ namePath, disabled }) => {
  const $t = useFormatMessage();

  return (
    <>
      <PortionForm namePath={namePath} disabled={disabled} />
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Form.Item name={[...namePath, 'type']} label="Type">
            <Select options={[{ label: 'Time on website', value: 'delayed' }, { label: 'Embedded', value: 'embedded' }, { label: 'Custom trigger', value: 'existingButton' }]} disabled={disabled} />
          </Form.Item>
        </Col>
        <FormDependency name={[...namePath, 'type']} show={value => value === 'existingButton'}>
          <Col span={12}>
            <Form.Item name={[...namePath, 'elementSelector']} label={<FormattedMessage {...messages.buttonSelector} />}>
              <Input placeholder={$t(messages.buttonSelectorPlaceholder)} />
            </Form.Item>
          </Col>
        </FormDependency>
        <FormDependency name={[...namePath, 'type']} show={value => value === 'embedded'}>
          <Col span={12}>
            <Form.Item name={[...namePath, 'elementSelector']} label={<FormattedMessage {...messages.containerSelector} />}>
              <Input placeholder={$t(messages.containerSelectorPlaceholder)} />
            </Form.Item>
          </Col>
        </FormDependency>
      </Row>
      <FormDependency name={[...namePath, 'type']} show={value => value === 'delayed'}>
        <TimerForm namePath={namePath} disabled={disabled} />
      </FormDependency>
      <Form.Item noStyle name={[...namePath, 'container']}>
        <input type="hidden" />
      </Form.Item>
      {/* <FormDependency name={[...namePath, 'type']} show={value => ['delayed', 'existingButton'].includes(value)}>
        <DisplayStyleForm namePath={namePath} disabled={disabled} />
      </FormDependency> */}
    </>
  );
};

const AudienceForm = ({ namePath, disabled, type }) => (
  <>
    {type === SurveyType.General && <GeneralSurveyForm namePath={namePath} disabled={disabled} />}
    {type === SurveyType.Feedback && <FeedbackForm namePath={namePath} disabled={disabled} />}
    {type === SurveyType.Targeted && <TargetedSurveyForm namePath={namePath} disabled={disabled} />}
  </>
);

const AudienceSettings = ({ name, survey, device, isTranslation }) => {
  const namePath = [name, survey.id, device];
  const setting = Form.useWatch([...namePath, 'setting']);
  const showSettings = setting === SurveySetting.Custom || setting === SurveySetting.Default;
  const options = getOptions({ device, isTranslation });

  return (
    <>
      <Form.Item noStyle name={[...namePath, 'distributionId']}>
        <input type="hidden" />
      </Form.Item>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name={[...namePath, 'setting']}>
            <Select options={options} />
          </Form.Item>
        </Col>
      </Row>
      {showSettings && (
        <AudienceForm namePath={namePath} disabled={setting === SurveySetting.Default} type={survey.type} />
      )}
    </>
  );
};

const DeviceSettings = (props) => (
  <Collapse accordion defaultActiveKey={DeviceType.Desktop}>
    {Object.values(DeviceType).map(d => {
      const device = d.toLowerCase();
      const namePath = [props.name, props.survey.id, device, 'setting'];

      return (
        <Panel
          header={(
            <>
              <DeviceTypeIcons deviceTypes={[d]} />
              <FormattedMessage {...messages[device]} />
            </>
          )}
          extra={(
            <Form.Item noStyle shouldUpdate={shouldUpdateDependency(namePath)}>
              {({ getFieldValue }) => <FormattedMessage {...messages[getFieldValue(namePath)]} />}
            </Form.Item>
          )}
          key={d}
        >
          <AudienceSettings device={device} {...props} />
        </Panel>
      );
    })}
  </Collapse>
);

const AudienceStep = ({ name, survey, translations }) => (
  <>
    <h2>
      Survey behavior
      <InputInfoCircle title={<FormattedMessage {...messages.behaviorHelpText} />} iconStyle={{ marginLeft: 8, width: 16 }} />
    </h2>
    <TranslationTabs
      primary={survey}
      translations={translations}
      primaryContent={<DeviceSettings name={name} survey={survey} />}
      translationsContent={translations.map(translation => <DeviceSettings name={name} survey={translation} isTranslation />)}
    />
  </>
);

export default AudienceStep;
